/* eslint-disable react/jsx-props-no-spreading */
import ItemCard from '@cont/Course/ItemCard';
import styled from '@emotion/styled';
import PDFDownload from '@cont/Course/PDFDownload';
import { useCallback, useState } from 'react';
import Menu from '@base/Menu/Menu';
import { isPost } from '@models/Item';
import { BACKEND_API_ERROR, savedObjectTypes } from '@constants/index';
import { removeEntityFromSaved } from '@actions/saved';
import { getSavedContentType } from '@utils/segment/propsHelper';
import { useDispatch } from 'react-redux';
import { useSnackbar } from '@stores/snackbar';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { SAVE_UNSAVE_CONTENT } from '@constants/segment';
import { segment } from '@actions/analytics';
import Dialog from '@comp/Dialog/Dialog';
import { mediaQuery, Spacings } from '@styles/index';
import mergeDeep from '@utils/mergeDeep';
import { processLessonData } from '@api/processors/lesson';

const StyledItemCard = styled(ItemCard)`
  .itemContainer {
    box-shadow: ${({ $isShadowBox }) => ($isShadowBox ? '' : 'none')};
    border: ${({ $isShadowBox }) =>
      $isShadowBox ? '' : '1px solid var(--color-divider)'};
    margin-bottom: ${({ $isShadowBox }) =>
      $isShadowBox ? '' : Spacings.SPACING_4B};
  }
  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const getFormattedLesson = (lesson) => {
  const formattedLesson = processLessonData(
    mergeDeep(lesson, lesson.properties, 2)
  );
  if (!formattedLesson.practiceDpp) return formattedLesson;

  return {
    ...formattedLesson,
    liveClassDetails: {
      practiceDpp: formattedLesson.practiceDpp
    }
  };
};

const RecordedBatchItem = ({ lesson: data, goalUID, ...rest }) => {
  const lesson = getFormattedLesson(data);
  const dispatch = useDispatch();
  const { open: showSnackbar } = useSnackbar();
  const goalSegmentData = useGoalEventProps({ goalUID });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [isUnEnrollModalOpen, setIsUnEnrollModalOpen] = useState(false);
  const setPDF = (elem) => {
    setAnchorEl(elem);
  };
  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const onCloseModal = () => {
    setIsUnEnrollModalOpen(false);
    setSelectedLesson(null);
  };

  const showRemoveDialog = () => {
    setSelectedLesson(lesson);
    setIsUnEnrollModalOpen(true);
  };

  const removeFromCollection = () => {
    if (selectedLesson) {
      // const goalUID = selectedLesson.goal?.uid || selectedLesson.goalUid;
      const isLesson = isPost(selectedLesson);
      const entityType = isLesson
        ? savedObjectTypes.LESSON
        : savedObjectTypes.TEST;
      dispatch(
        removeEntityFromSaved({
          goalUid: goalUID,
          objectUid: selectedLesson.uid,
          entityType,
          key: `${goalUID}-${isLesson ? 'lessons' : 'tests'}`
        })
      ).then((res) => {
        if (res?.result) {
          showSnackbar({
            message: `${isLesson ? 'Lesson' : 'Test'} removed successfully`,
            time: 5000,
            type: 'success'
          });
          segment.track(SAVE_UNSAVE_CONTENT, {
            save_content_type: getSavedContentType(entityType),
            save_content_uid: selectedLesson.uid,
            save_content_title: selectedLesson.name,
            ...goalSegmentData
          });
        } else {
          showSnackbar({
            message: BACKEND_API_ERROR,
            time: 5000,
            type: 'error'
          });
        }
      });
    }
    onCloseModal();
  };

  return (
    <>
      <StyledItemCard
        lesson={lesson}
        goalUID={goalUID}
        setPdfAnchorEl={setPDF}
        showRemoveDialog={showRemoveDialog}
        $isShadowBox={rest.isShadowBox}
        {...rest}
      />
      {anchorEl && (
        <Menu
          anchorEl={anchorEl}
          onClose={handleClose}
          position="right"
          width="231px"
        >
          <PDFDownload
            selectedLesson={{
              slides: {
                withAnnotations: lesson?.slidesPdf?.withAnnotation,
                withoutAnnotations: lesson?.slidesPdf?.noAnnotation
              }
            }}
            handleClose={handleClose}
          />
        </Menu>
      )}
      <Dialog
        onClose={onCloseModal}
        dialogState={isUnEnrollModalOpen}
        heading="Remove from Saved?"
        message={`This will remove the ${
          isPost(selectedLesson) ? 'lesson' : 'test'
        } from your saved list. You can add it back from the ${
          isPost(selectedLesson) ? 'course' : 'test series'
        } page.`}
        messageWidth="82.5%"
        acceptLabel="Remove"
        rejectLabel="Cancel"
        acceptOnClick={removeFromCollection}
        rejectOnClick={onCloseModal}
      />
    </>
  );
};

export default RecordedBatchItem;
